const { merge } = require('lodash');

module.exports = class SplunkClientLogger {
  constructor(config) {
    // TODO: use these options to control things like printing to console or remote logging certain types
    this.config = config || {};

    if (this.config.configUrl) {
      // Get config from URL here. Should be a JSON file.
      setConfigFromUrl(this.config.configUrl);
    } else {
      if (!this.config.endpoint) {
        throw 'endpoint is required.';
      }

      if (!this.config.token) {
        throw 'token is required.';
      }

      if (!this.config.index) {
        throw 'index is required.';
      }

      if (!this.config.sourcetype) {
        throw 'sourcetype is required.';
      }

      // Default settings if not passed by user
      this.config.console = this.config.console || {
        isEnabled: true
      };
      this.config.debugMode = this.config.debugMode || false;
      this.config.endpoint = this.config.endpoint;
      this.config.token = this.config.token || null;
      this.config.attrs = this.config.attrs || {};
      this.config.additionalMeta = this.config.additionalMeta || {};
      this.config.ua = this.config.ua || null;
      this.config.osName = this.config.osName || null;
      this.config.device = this.config.device || null;
    }

    // From: https://console.spec.whatwg.org/
    this.logLevels = [
      'debug',
      'error',
      'info',
      'log',
      'warn'
    ];

    this.captureConsole();
    this.captureErrors();
  }

  // TODO: validate payload and catch errors.
  // Throw errors if any required bits are missing.
  async setConfigFromUrl(url) {
    let file = await fetch(url);
    let json = await file.json();
    this.config = json;
  }

  captureErrors() {
    let logger = this;

    window.onerror = function (msg, url, lineNo, columnNo, error) {
      let messageString = msg.toLowerCase();

      /*
      When an error occurs in a script, loaded from a different origin, the
      details of the error are not reported to prevent leaking information
      (see bug 363897). Instead the error reported is simply "Script error."
      This behavior can be overriden in some browsers using the crossorigin
      attribute on <script> and having the server send the appropriate CORS
      HTTP response headers.  A workaround is to isolate "Script error." and
      handle it knowing that the error detail is only viewable in the browser
      console and not accessible via JavaScript.
      */

      let substring = 'script error';

      if (messageString.indexOf(substring) > -1) {
        logger.sendLogs('Script error', 'error');
      } else {
        let errorObject = {
          message: msg,
          url: url,
          line: lineNo,
          column: columnNo,
          errorObject: JSON.stringify(error)
        };

        logger.sendLogs(errorObject, 'error');
      }

      return false;
    };
  }

  captureConsole() {
    let logger = this;

    if (logger.config.console.isEnabled !== false) {
      logger.logLevels.forEach(function(level) {
        if (logger.config.console.isEnabled[level] !== false) {
          let originalLogger = window.console[level];
          window.console[level] = function() {
            // By converting arguments to a real array, we can now work with it
            // however we choose (e.g. modify all args before printing them: args.push('hello, world');)
            let args = [...arguments];
            originalLogger.apply(window.console, args);
            logger.sendLogs(args, level);
          };
        }
      });
    }
  }

  // Make this configurable with options to turn on/off local and remote logging
  // TODO: Add ability to filter actions by type
  reduxMiddleware(config = {}) {
    let logger = this;

    return store => next => action => {
      let data = {
        action: null,
        state: null
      };

      try {
        data.action = action;
        data.state = store.getState();

        if (config.includeFromState) {
          if (typeof config.includeFromState === 'function') {
            logger.config.additionalMeta['state'] = config.includeFromState(data.state);
          } else {
            throw 'includeFromState must be a function';
          }
        }

        if (logger.config.debugMode !== false) {
          logger.info('Dispatching', action);
        }

        let result = next(action);

        if (logger.config.debugMode !== false) {
          logger.info('Next state', store.getState());
        }
        return result;
      } catch (err) {
        // Log the error along with the current action and state
        logger.error(err, data);
        throw err;
      }
    };
  }

  debug(...args) {
    this.sendLogs(args, 'debug');
  }

  error(...args) {
    this.sendLogs(args, 'error');
  }

  info(...args) {
    this.sendLogs(args, 'info');
  }

  log(...args) {
    this.sendLogs(args, 'log');
  }

  warn(...args) {
    this.sendLogs(args, 'warn');
  }

  // Designed to take two args, but can accept any number of args. Example:
  // Logger.log('message', {foo: 'bar'})
  sendLogs(args = [], level = 'log') {
    try {
      let payload = {};
      let message = '';

      if (args.length === 1) {
        if (typeof args[0] === 'string') {
          message = args[0];
        } else if (typeof args[0] === 'object') {
          payload = args[0];
        }
      } else if (args.length <= 2 && typeof args[0] === 'string' && typeof args[1] === 'object') {
        message = args[0];
        payload = args[1];
      } else {
        payload = args.map((arg) => {
          if (typeof arg === 'undefined') {
            return 'undefined';
          } else if (typeof arg === 'object') {
            return JSON.stringify(arg);
          } else {
            return arg.toString();
          }
        }).join(' ');
      }

      payload.ua = this.config.ua;
      payload.osName = this.config.osName;
      payload.device = this.config.device;

      fetch(this.config.endpoint, {
        method: 'POST',
        headers: merge(this.config.headers, {
          'Authorization': `Splunk ${this.config.token}`,
          'Content-Type': 'text/plain'
        }),
        body: JSON.stringify({
          index: this.config.index,
          sourcetype: this.config.sourcetype,
          event: {
            data: payload,
            level: level.toUpperCase(),
            time: new Date().toISOString(),
            message: message,
            source: this.config.source,
            referer: window.location.href,
            origin: window.location.origin,
            meta: this.config.additionalMeta
          }
        }),
        keepalive: true
      });
    } catch (err) {
      throw err;
    }
  }
};


//////////////////
// WEBPACK FOOTER
// ./~/@its-svp/splunk-client-logger/src/logger.js
// module id = 856
// module chunks = 0